import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class Md5BypassService {

  /**
   * Genera un timestamp redondeado a la hora actual
   * Esto asegura que el hash cambie cada hora
   */
  private getHourlyTimestamp(date?: Date): string {
    const now = date || new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');

    return `${year}-${month}-${day}-${hour}`;
  }

  /**
   * Genera el hash MD5 dinámico basado en la frase secreta + timestamp por hora
   * @param customDate - Fecha personalizada para testing (opcional)
   */
  generateMd5Hash(customDate?: Date): string {
    if (!environment.md5Config?.secretPhrase) {
      throw new Error('No se ha configurado una frase secreta para MD5');
    }

    const hourlyTimestamp = this.getHourlyTimestamp(customDate);
    const combinedPhrase = `${environment.md5Config.secretPhrase}-${hourlyTimestamp}`;

    const hash = CryptoJS.MD5(combinedPhrase).toString();

    // Log para desarrollo (solo en modo no-producción)
    if (!environment.production) {
      console.log(`🔐 Hash MD5 generado para ${hourlyTimestamp}:`, hash);
      console.log(`📝 Frase combinada: ${combinedPhrase}`);
    }

    return hash;
  }

  /**
   * Verifica si el parámetro MD5 en la URL actual es válido
   * Incluye validación para la hora actual y la anterior (ventana de gracia)
   */
  checkMd5Bypass(): boolean {
    try {
      if (!environment.md5Config) {
        return false;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const providedHash = urlParams.get(environment.md5Config.urlParamName);

      if (!providedHash) {
        return false;
      }

      // Verificar hash para la hora actual
      const currentHash = this.generateMd5Hash();
      if (providedHash === currentHash) {
        console.log('✅ Bypass MD5 válido para hora actual');
        return true;
      }

      // Verificar hash para la hora anterior (ventana de gracia de 1 hora)
      const previousHour = new Date();
      previousHour.setHours(previousHour.getHours() - 1);
      const previousHash = this.generateMd5Hash(previousHour);

      if (providedHash === previousHash) {
        console.log('⚠️ Bypass MD5 válido para hora anterior (ventana de gracia)');
        return true;
      }

      console.log('❌ Hash MD5 no válido o expirado');
      return false;
    } catch (error) {
      console.error('Error verificando bypass MD5:', error);
      return false;
    }
  }

  /**
   * Genera una URL completa con el bypass MD5 dinámico
   */
  generateBypassUrl(baseUrl?: string): string {
    if (environment.production) {
      console.warn('⚠️ No se recomienda generar URLs de bypass en producción');
    }

    const currentUrl = baseUrl || window.location.origin + window.location.pathname;
    const hash = this.generateMd5Hash();
    const paramName = environment.md5Config?.urlParamName || 'dev_tools';

    const separator = currentUrl.includes('?') ? '&' : '?';
    return `${currentUrl}${separator}${paramName}=${hash}`;
  }

  /**
   * Genera múltiples URLs de bypass (hora actual y siguiente)
   * Útil para desarrolladores que necesiten URLs válidas por más tiempo
   */
  generateExtendedBypassUrls(baseUrl?: string): {
    current: { hour: string; url: string; hash: string };
    next: { hour: string; url: string; hash: string };
    previous: { hour: string; url: string; hash: string };
  } {
    const currentUrl = baseUrl || window.location.origin + window.location.pathname;
    const paramName = environment.md5Config?.urlParamName || 'dev_tools';
    const separator = currentUrl.includes('?') ? '&' : '?';

    // Hora actual
    const now = new Date();
    const currentHash = this.generateMd5Hash(now);
    const currentHour = this.getHourlyTimestamp(now);

    // Próxima hora
    const nextHour = new Date(now);
    nextHour.setHours(nextHour.getHours() + 1);
    const nextHash = this.generateMd5Hash(nextHour);
    const nextHourStr = this.getHourlyTimestamp(nextHour);

    // Hora anterior
    const prevHour = new Date(now);
    prevHour.setHours(prevHour.getHours() - 1);
    const prevHash = this.generateMd5Hash(prevHour);
    const prevHourStr = this.getHourlyTimestamp(prevHour);

    return {
      current: {
        hour: currentHour,
        hash: currentHash,
        url: `${currentUrl}${separator}${paramName}=${currentHash}`
      },
      next: {
        hour: nextHourStr,
        hash: nextHash,
        url: `${currentUrl}${separator}${paramName}=${nextHash}`
      },
      previous: {
        hour: prevHourStr,
        hash: prevHash,
        url: `${currentUrl}${separator}${paramName}=${prevHash}`
      }
    };
  }

  /**
   * Genera información para desarrolladores sobre cómo usar el bypass dinámico
   */
  getBypassInfo(): {
    hash: string;
    paramName: string;
    exampleUrl: string;
    validUntil: string;
    nextHash?: string;
    nextValidFrom?: string;
  } | null {
    if (!environment.md5Config) {
      return null;
    }

    try {
      const hash = this.generateMd5Hash();
      const paramName = environment.md5Config.urlParamName;
      const exampleUrl = this.generateBypassUrl();

      // Calcular cuándo expira el hash actual
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0); // Siguiente hora en punto

      const nextHash = this.generateMd5Hash(nextHour);

      return {
        hash,
        paramName,
        exampleUrl,
        validUntil: nextHour.toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
          hour12: false
        }),
        nextHash,
        nextValidFrom: nextHour.toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
          hour12: false
        })
      };
    } catch (error) {
      console.error('Error generando información de bypass:', error);
      return null;
    }
  }

  /**
   * Método de utilidad para desarrolladores - imprime información completa de bypass
   */
  logBypassInfo(): void {
    if (environment.production) {
      return;
    }

    const info = this.getBypassInfo();
    const extendedUrls = this.generateExtendedBypassUrls();

    if (info) {
      console.group('🔓 Sistema de Bypass MD5 Dinámico');
      console.log('⏰ Hora actual:', new Date().toLocaleString('es-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      }));
      console.log('🔑 Hash actual:', info.hash);
      console.log('📅 Válido hasta:', info.validUntil);
      console.log('🔗 URL actual:', info.exampleUrl);

      if (info.nextHash) {
        console.log('🔑 Próximo hash:', info.nextHash);
        console.log('📅 Válido desde:', info.nextValidFrom);
      }

      console.group('📋 URLs extendidas');
      console.log('⏮️ Hora anterior:', extendedUrls.previous.url);
      console.log('⏯️ Hora actual:', extendedUrls.current.url);
      console.log('⏭️ Próxima hora:', extendedUrls.next.url);
      console.groupEnd();

      console.log('ℹ️ Los hashes cambian cada hora y tienen 1 hora de gracia');
      console.groupEnd();
    }
  }

  /**
   * Método de utilidad para obtener el tiempo restante hasta el próximo cambio de hash
   */
  getTimeUntilNextHash(): { minutes: number; seconds: number; totalSeconds: number } {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);

    const totalSeconds = Math.floor((nextHour.getTime() - now.getTime()) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return { minutes, seconds, totalSeconds };
  }
}
